/**
 * What this browser tells the API about itself, for the backend's request log.
 *
 * Pure helpers: every function takes its inputs as parameters (defaulting to `navigator`) so the
 * specs can feed them any user agent. Nothing here reads or writes storage.
 *
 * Same logic as the User Platform's `src/app/shared/utils/device-info.ts` (aoc-frontend-new), so
 * both apps report devices the same way to the one backend.
 *
 * The values travel as HTTP request headers (see DeviceInfoInterceptor), and a header value that
 * is not ISO-8859-1 makes the browser reject the whole request. So every value built here comes
 * from fixed ASCII names plus digits, never from raw user-agent text.
 */

export type DeviceType = 'desktop' | 'mobile' | 'tablet';

/** The backend accepts `x-browser` up to this length and cuts anything longer. */
const MAX_BROWSER_LENGTH = 100;

/**
 * Checked in order, first match wins: in-app and Chromium-based browsers also carry a `Chrome/`
 * token, and Chrome carries a `Safari/` one, so the more specific rules must come first.
 */
const BROWSER_RULES: ReadonlyArray<readonly [name: string, pattern: RegExp]> = [
  ['MetaMask Mobile', /MetaMaskMobile/i],
  ['Edge', /Edg(?:e|A|iOS)?\/(\d+)/],
  ['Opera', /(?:OPR|OPT)\/(\d+)/],
  ['Samsung Internet', /SamsungBrowser\/(\d+)/],
  ['UC Browser', /UCBrowser\/(\d+)/],
  ['Firefox', /(?:Firefox|FxiOS)\/(\d+)/],
  ['Chrome', /CriOS\/(\d+)/],
  ['Android WebView', /; wv\).*Chrome\/(\d+)/],
  ['Chrome', /Chrome\/(\d+)/],
  ['Safari', /Version\/(\d+)[\d.]*.*Safari\//],
];

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function readUserAgent(): string {
  return typeof navigator === 'undefined' ? '' : navigator.userAgent ?? '';
}

function readPlatform(): string {
  return typeof navigator === 'undefined' ? '' : navigator.platform ?? '';
}

function readMaxTouchPoints(): number {
  return typeof navigator === 'undefined' ? 0 : navigator.maxTouchPoints ?? 0;
}

/**
 * Classifies the device as the backend expects it: exactly `desktop`, `mobile` or `tablet`
 * (any other value is stored as null).
 * @param userAgent the browser's user agent
 * @param platform `navigator.platform`, used to spot iPadOS
 * @param maxTouchPoints `navigator.maxTouchPoints`, used to spot iPadOS
 * @returns the device class
 */
export function getDeviceType(
  userAgent: string = readUserAgent(),
  platform: string = readPlatform(),
  maxTouchPoints: number = readMaxTouchPoints(),
): DeviceType {
  // Android tablets are the Android user agents without a "Mobile" token.
  if (/iPad|Tablet|PlayBook|Silk|Kindle|Android(?!.*Mobile)/i.test(userAgent)) return 'tablet';
  // iPadOS 13+ Safari reports a desktop Mac user agent; touch support is the only tell.
  if (platform === 'MacIntel' && maxTouchPoints > 1) return 'tablet';
  if (/Mobi|iPhone|iPod|Android|Windows Phone|IEMobile|BlackBerry|BB10|Opera Mini/i.test(userAgent)) return 'mobile';
  return 'desktop';
}

/**
 * Names the browser and its major version, e.g. `Chrome 128` or `MetaMask Mobile`.
 * @param userAgent the browser's user agent
 * @returns the browser label, `Other` for an unrecognised user agent, or null when there is none
 */
export function getBrowser(userAgent: string = readUserAgent()): string | null {
  if (!userAgent) return null;
  for (const [name, pattern] of BROWSER_RULES) {
    const match = userAgent.match(pattern);
    if (match) {
      const label = match[1] ? `${name} ${match[1]}` : name;
      return label.slice(0, MAX_BROWSER_LENGTH);
    }
  }
  return 'Other';
}

/**
 * Whether a value is a device id as the backend issues it (a UUID). The backend stores any other
 * `x-device-id` value as null, so nothing else is kept or sent.
 * @param value the candidate
 * @returns true for a UUID string
 */
export function isDeviceId(value: unknown): value is string {
  return typeof value === 'string' && UUID_PATTERN.test(value);
}
